import React, { useState, useEffect } from 'react';
import { useNavigate, Link } from 'react-router-dom';
import { supabase } from '../lib/supabase';
import { CheckCircle, XCircle, Loader } from 'lucide-react';

const PASSWORD_REQUIREMENTS = [
  { id: 'length', label: 'At least 8 characters long', regex: /.{8,}/ },
  { id: 'uppercase', label: 'At least one uppercase letter', regex: /[A-Z]/ },
  { id: 'lowercase', label: 'At least one lowercase letter', regex: /[a-z]/ },
  { id: 'number', label: 'At least one number', regex: /[0-9]/ },
  { id: 'special', label: 'At least one special character', regex: /[!@#$%^&*(),.?":{}|<>]/ }
];

enum ResetStatus {
  FORM = 'form',
  PROCESSING = 'processing',
  SUCCESS = 'success',
  ERROR = 'error'
}

export default function ResetPassword() {
  const [password, setPassword] = useState('');
  const [confirmPassword, setConfirmPassword] = useState('');
  const [status, setStatus] = useState<ResetStatus>(ResetStatus.FORM);
  const [error, setError] = useState('');
  const navigate = useNavigate();

  useEffect(() => {
    // Check if we have the access token in the URL
    const hash = window.location.hash;
    if (!hash || !hash.includes('access_token')) {
      setStatus(ResetStatus.ERROR);
      setError('Invalid or missing reset token. Please request a new password reset link.');
    }
  }, []);

  const checkPasswordRequirements = (password: string) => {
    return PASSWORD_REQUIREMENTS.every(req => req.regex.test(password));
  };

  const getPasswordStrength = (password: string) => {
    if (!password) return [];
    return PASSWORD_REQUIREMENTS.filter(req => req.regex.test(password));
  };

  const handleSubmit = async (e: React.FormEvent) => {
    e.preventDefault();
    
    if (password !== confirmPassword) {
      setError('Passwords do not match');
      return;
    }

    if (!checkPasswordRequirements(password)) {
      setError('Password does not meet all requirements');
      return;
    }

    setStatus(ResetStatus.PROCESSING);
    
    try {
      // Supabase will automatically extract the token from the URL
      const { error } = await supabase.auth.updateUser({ password });
      
      if (error) {
        throw error;
      }
      
      setStatus(ResetStatus.SUCCESS);
      
      // Auto-redirect to login after 5 seconds
      setTimeout(() => {
        navigate('/login');
      }, 5000);
    } catch (err) {
      setStatus(ResetStatus.ERROR);
      setError(err instanceof Error ? err.message : 'Failed to reset password');
    }
  };

  const metRequirements = getPasswordStrength(password);
  const isValid = checkPasswordRequirements(password) && password === confirmPassword;

  return (
    <div className="min-h-screen bg-gray-50 flex flex-col justify-center py-12 sm:px-6 lg:px-8">
      <div className="sm:mx-auto sm:w-full sm:max-w-md">
        <img
          className="mx-auto h-16 w-auto"
          src="/assets/araclogo2.png"
          alt="ARAC Logo"
        />
        <h2 className="mt-6 text-center text-3xl font-bold tracking-tight text-gray-900">
          Reset Your Password
        </h2>
      </div>

      <div className="mt-8 sm:mx-auto sm:w-full sm:max-w-md">
        <div className="bg-white py-8 px-4 shadow sm:rounded-lg sm:px-10">
          {status === ResetStatus.FORM && (
            <form className="space-y-6" onSubmit={handleSubmit}>
              {error && (
                <div className="bg-red-50 border border-red-200 text-red-700 px-4 py-3 rounded relative">
                  {error}
                </div>
              )}

              <div>
                <label htmlFor="password" className="block text-sm font-medium text-gray-700">
                  New Password
                </label>
                <input
                  type="password"
                  id="password"
                  value={password}
                  onChange={(e) => setPassword(e.target.value)}
                  required
                  className="mt-1 block w-full rounded-md border border-gray-300 px-3 py-2 shadow-sm focus:border-primary focus:outline-none focus:ring-primary sm:text-sm"
                />
                <div className="mt-2 space-y-2">
                  {PASSWORD_REQUIREMENTS.map(req => (
                    <div
                      key={req.id}
                      className={`flex items-center text-sm ${
                        metRequirements.includes(req) ? 'text-green-600' : 'text-gray-400'
                      }`}
                    >
                      <span className="mr-2">
                        {metRequirements.includes(req) ? '✓' : '○'}
                      </span>
                      {req.label}
                    </div>
                  ))}
                </div>
              </div>

              <div>
                <label htmlFor="confirmPassword" className="block text-sm font-medium text-gray-700">
                  Confirm Password
                </label>
                <input
                  type="password"
                  id="confirmPassword"
                  value={confirmPassword}
                  onChange={(e) => setConfirmPassword(e.target.value)}
                  required
                  className="mt-1 block w-full rounded-md border border-gray-300 px-3 py-2 shadow-sm focus:border-primary focus:outline-none focus:ring-primary sm:text-sm"
                />
                {password && confirmPassword && password !== confirmPassword && (
                  <p className="mt-1 text-sm text-red-600">Passwords do not match</p>
                )}
              </div>

              <button
                type="submit"
                disabled={!isValid}
                className={`w-full flex justify-center py-2 px-4 border border-transparent rounded-md shadow-sm text-sm font-medium text-white ${
                  isValid
                    ? 'bg-primary hover:bg-primary-dark focus:outline-none focus:ring-2 focus:ring-offset-2 focus:ring-primary'
                    : 'bg-gray-300 cursor-not-allowed'
                }`}
              >
                Reset Password
              </button>
            </form>
          )}

          {status === ResetStatus.PROCESSING && (
            <div className="text-center py-6">
              <Loader className="h-12 w-12 text-primary mx-auto animate-spin" />
              <p className="mt-4 text-gray-600">Resetting your password...</p>
            </div>
          )}

          {status === ResetStatus.SUCCESS && (
            <div className="text-center py-6">
              <CheckCircle className="h-16 w-16 text-green-500 mx-auto" />
              <h3 className="mt-4 text-xl font-medium text-gray-900">Password Reset Successfully!</h3>
              <p className="mt-2 text-gray-600">
                Your password has been reset. You can now sign in with your new password.
              </p>
              <p className="mt-1 text-sm text-gray-500">
                You will be redirected to the login page in 5 seconds.
              </p>
              <div className="mt-6">
                <Link
                  to="/login"
                  className="w-full flex justify-center py-2 px-4 border border-transparent rounded-md shadow-sm text-sm font-medium text-white bg-primary hover:bg-primary-dark focus:outline-none focus:ring-2 focus:ring-offset-2 focus:ring-primary"
                >
                  Go to Login
                </Link>
              </div>
            </div>
          )}

          {status === ResetStatus.ERROR && (
            <div className="text-center py-6">
              <XCircle className="h-16 w-16 text-red-500 mx-auto" />
              <h3 className="mt-4 text-xl font-medium text-gray-900">Password Reset Failed</h3>
              <p className="mt-2 text-gray-600">
                {error || 'There was a problem resetting your password.'}
              </p>
              <div className="mt-6">
                <Link
                  to="/login"
                  className="w-full flex justify-center py-2 px-4 border border-transparent rounded-md shadow-sm text-sm font-medium text-white bg-primary hover:bg-primary-dark focus:outline-none focus:ring-2 focus:ring-offset-2 focus:ring-primary"
                >
                  Back to Login
                </Link>
              </div>
            </div>
          )}
        </div>
      </div>
    </div>
  );
}
